<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  mdiAccountGroup,
  mdiBell,
  mdiBook,
  mdiCalendarClock,
  mdiCartVariant,
  mdiCashRegister,
  mdiCityVariantOutline,
  mdiClipboardArrowLeft,
  mdiClipboardCheckMultipleOutline,
  mdiFileArrowLeftRightOutline,
  mdiFileDocumentMultiple,
  mdiFileSign,
  mdiFinance,
  mdiFoodForkDrink,
  mdiHandshake,
  mdiLogout,
  mdiMessage,
  mdiOfficeBuilding,
  mdiOfficeBuildingMarkerOutline,
  mdiPuzzle,
  mdiRoutes,
  mdiTicketPercentOutline,
  mdiTruckDelivery,
  mdiViewDashboard,
  mdiWeatherNight,
  mdiWeatherSunny,
} from '@mdi/js'
import { storeToRefs } from 'pinia'
import { isAccountant, isGetDeliverOrBiller, isOwner, ROLE } from '../../helpers/roles'
import { isSandwiczSzop } from '../../helpers/company'
import { mapDateFromFirebaseFormat } from '../../helpers/time'
import { useVuetify } from '../../useVuetify'
import i18n from '~/i18n'
import { useDrawerStore } from '~/store/drawer'
import { useMessagesStore } from '~/store/messages'
import { useReportBugOrSuggestionStore } from '~/store/reportBugOrSuggestion'
import type { ReportBugOrSuggestionModel } from '~/models/ReportBugOrSuggestionModel'
import { useCompanyStore } from '~/store/company'
import { useAppStore } from '~/store/app'

const appStore = useAppStore()
const { userData, user } = storeToRefs(appStore)

const drawerStore = useDrawerStore()
const { visible } = storeToRefs(drawerStore)

const { setTheme } = useVuetify()

const isDark = ref(false)
const showAgreementDialog = ref(false)

const permissions = computed(() => userData.value.permissions)
const role = computed(() => userData.value?.role)

const deliversSellsAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasDeliversSellsAccess : false))
const employeesAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasEmployeesAccess : false))
const routesAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasRoutesAccess : false))
const menuReturnsAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasMenuReturnsAccess : false))
const menuReturnsReportAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasMenuReturnsReportAccess : false))
const changeLogAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasChangeLogAccess : false))
// const localizationsAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasLocalizationAccess : false))
const documentsAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasDocumentsAccess : false))
const deliversOrdersAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasDeliversOrdersAccess : false))
const menuAccess = computed(() => (isGetDeliverOrBiller(role.value) ? permissions.value.hasMenuAccess : false))

const ModeratorPanel = computed(() => [
  {
    icon: mdiViewDashboard,
    title: i18n.t('Navigation.admin'),
    link: '/moderator',
  },
  {
    icon: mdiOfficeBuilding,
    title: i18n.t('Navigation.companies'),
    link: '/companies',
  },
  {
    icon: mdiMessage,
    title: i18n.t('Navigation.messages'),
    link: '/messages',
  },
  {
    divider: true,
  },
  {
    icon: mdiLogout,
    title: i18n.t('Navigation.sign-out'),
    link: '/sign-out',
  },
])
const ManagerPanel = computed(() => [
  {
    icon: mdiRoutes,
    title: i18n.t('Navigation.routes'),
    link: '/routes',
  },
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('Navigation.suppliers'),
    link: '/suppliers',
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.search'),
        link: '/suppliers',
      },
      {
        title: i18n.t('Navigation.map'),
        link: '/suppliersMap',
      },
    ],
  },
  {
    icon: mdiFoodForkDrink,
    title: i18n.t('Navigation.menu'),
    link: '/menu',
  },
  {
    icon: mdiClipboardArrowLeft,
    title: i18n.t('Navigation.menuReturns'),
    link: '/menuReturn',
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.menuReturns'),
        link: '/menuReturn',
      },
      {
        title: i18n.t('Navigation.menuReturnsReport'),
        link: '/menuReturnReport',
      },
    ],
  },
  {
    icon: mdiTruckDelivery,
    title: i18n.t('Navigation.deliversOrders'),
    link: '/deliversOrders',
  },
  {
    icon: mdiCalendarClock,
    title: i18n.t('Navigation.news'),
    link: '/news',
  },
  {
    icon: mdiFileDocumentMultiple,
    title: i18n.t('Navigation.invoices'),
    link: '/invoices',
  },
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('DeliversSellsView.title'),
    link: '/delivers-sells',
  },
  {
    icon: mdiCityVariantOutline,
    title: i18n.t('Navigation.facilities'),
    subGroup: true,
    items: [
      {
        icon: mdiCityVariantOutline,
        title: i18n.t('Navigation.suppliersSubsidy'),
        link: '/suppliersSubsidy',
      },
      {
        icon: mdiHandshake,
        title: i18n.t('Navigation.subsidy'),
        link: '/subsidy',
      },
    ],
  },
  {
    icon: mdiAccountGroup,
    title: i18n.t('Navigation.employees'),
    link: '/employees',
    subGroup: true,
    items: [
      {
        icon: mdiAccountGroup,
        title: i18n.t('Navigation.employees'),
        link: '/employees',
      },
      {
        title: i18n.t('Navigation.employeesRanking'),
        link: '/employeesRanking',
      },
    ],
  },
  {
    icon: mdiClipboardCheckMultipleOutline,
    title: i18n.t('OnlineTransactionReports.title'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.stats'),
        link: '/ordersStatistic',
      },
      {
        title: i18n.t('Navigation.notyfication'),
        link: '/stats',
      },
      {
        title: i18n.t('Navigation.orders'),
        link: '/orders',
      },
      {
        title: i18n.t('OnlineTransactionReports.title2'),
        link: '/onlineTransactionReports',
      },
      {
        title: i18n.t('PromotionsReports.title'),
        link: '/promotionsReports',
      },
    ],
  },
  {
    icon: mdiTicketPercentOutline,
    title: i18n.t('Navigation.coupons'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.coupons'),
        link: '/coupons',
      },
      {
        title: i18n.t('Navigation.couponStatistics'),
        link: '/promo-code-statistics',
      },
      {
        title: i18n.t('Navigation.couponsUsed'),
        link: '/promo-code-usage',
      },
    ],
  },
  {
    icon: mdiFileSign,
    title: i18n.t('Navigation.documents'),
    link: '/documents',
  },
  {
    icon: mdiFileArrowLeftRightOutline,
    title: i18n.t('Navigation.changeReport'),
    link: '/change-report',
  },
  // {
  //   icon: mdiMapMarkerPath,
  //   title: i18n.t('Navigation.localization'),
  //   link: '/localizations',
  // },
  {
    icon: mdiFinance,
    title: i18n.t('Navigation.settlement'),
    link: '/summary',
  },
])
const BillerPanel = computed(() => [
  (routesAccess.value
    ? {
        icon: mdiRoutes,
        title: i18n.t('Navigation.routes'),
        link: '/routes',
      }
    : null
  ),
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('Navigation.suppliers'),
    link: '/suppliers',
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.search'),
        link: '/suppliers',
      },
      {
        title: i18n.t('Navigation.map'),
        link: '/suppliersMap',
      },
    ],
  },
  (employeesAccess.value
    ? {
        icon: mdiAccountGroup,
        title: i18n.t('Navigation.employees'),
        link: '/employees',
        subGroup: true,
        items: [
          {
            icon: mdiAccountGroup,
            title: i18n.t('Navigation.employees'),
            link: '/employees',
          },
          {
            title: i18n.t('Navigation.employeesRanking'),
            link: '/employeesRanking',
          },
        ],
      }
    : null
  ),
  (deliversSellsAccess.value
    ? {
        icon: mdiOfficeBuildingMarkerOutline,
        title: i18n.t('DeliversSellsView.title'),
        link: '/delivers-sells',
      }
    : null
  ),
  (menuReturnsAccess.value && menuReturnsReportAccess.value
    ? {
        icon: mdiClipboardArrowLeft,
        title: i18n.t('Navigation.menuReturns'),
        link: '/menuReturn',
        subGroup: true,
        items: [
          {
            title: i18n.t('Navigation.menuReturns'),
            link: '/menuReturn',
          },
          {
            title: i18n.t('Navigation.menuReturnsReport'),
            link: '/menuReturnReport',
          },
        ],
      }
    : menuReturnsAccess.value
      ? {
          icon: mdiClipboardArrowLeft,
          title: i18n.t('Navigation.menuReturns'),
          link: '/menuReturn',
        }
      : menuReturnsReportAccess.value
        ? {
            icon: mdiClipboardArrowLeft,
            title: i18n.t('Navigation.menuReturnsReport'),
            link: '/menuReturnReport',
          }
        : null
  ),
  (changeLogAccess.value
    ? {
        icon: mdiFileArrowLeftRightOutline,
        title: i18n.t('Navigation.changeReport'),
        link: '/change-report',
      }
    : null),
  (deliversOrdersAccess.value
    ? {
        icon: mdiTruckDelivery,
        title: i18n.t('Navigation.deliversOrders'),
        link: '/deliversOrders',
      }
    : null
  ),
  (documentsAccess.value
    ? {
        icon: mdiFileSign,
        title: i18n.t('Navigation.documents'),
        link: '/documents',
      }
    : null),
  // (localizationsAccess.value
  //   ? {
  //       icon: mdiMapMarkerPath,
  //       title: i18n.t('Navigation.localization'),
  //       link: '/localizations',
  //     }
  //   : null),
].filter(item => item !== null))
const DeliverPanel = computed(() => [
  {
    icon: mdiFileSign,
    title: i18n.t('Navigation.myDocuments'),
    link: '/my-documents',
  },
])
const GetDeliverPanel = computed(() => [
  (routesAccess.value
    ? {
        icon: mdiRoutes,
        title: i18n.t('Navigation.routes'),
        link: '/routes',
      }
    : null
  ),
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('Navigation.suppliers'),
    link: '/suppliers',
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.search'),
        link: '/suppliers',
      },
      {
        title: i18n.t('Navigation.map'),
        link: '/suppliersMap',
      },
    ],
  },
  (menuAccess.value
    ? {
        icon: mdiFoodForkDrink,
        title: i18n.t('Navigation.menu'),
        link: '/menu',
      }
    : null
  ),
  (menuReturnsAccess.value && menuReturnsReportAccess.value
    ? {
        icon: mdiClipboardArrowLeft,
        title: i18n.t('Navigation.menuReturns'),
        link: '/menuReturn',
        subGroup: true,
        items: [
          {
            title: i18n.t('Navigation.menuReturns'),
            link: '/menuReturn',
          },
          {
            title: i18n.t('Navigation.menuReturnsReport'),
            link: '/menuReturnReport',
          },
        ],
      }
    : menuReturnsAccess.value
      ? {
          icon: mdiClipboardArrowLeft,
          title: i18n.t('Navigation.menuReturns'),
          link: '/menuReturn',
        }
      : menuReturnsReportAccess.value
        ? {
            icon: mdiClipboardArrowLeft,
            title: i18n.t('Navigation.menuReturnsReport'),
            link: '/menuReturnReport',
          }
        : null
  ),
  {
    icon: mdiCalendarClock,
    title: i18n.t('Navigation.news'),
    link: '/news',
  },
  {
    icon: mdiFileDocumentMultiple,
    title: i18n.t('Navigation.invoices'),
    link: '/invoices',
  },

  (deliversSellsAccess.value
    ? {
        icon: mdiOfficeBuildingMarkerOutline,
        title: i18n.t('DeliversSellsView.title'),
        link: '/delivers-sells',
      }
    : null
  ),
  (employeesAccess.value
    ? {
        icon: mdiAccountGroup,
        title: i18n.t('Navigation.employees'),
        link: '/employees',
        subGroup: true,
        items: [
          {
            icon: mdiAccountGroup,
            title: i18n.t('Navigation.employees'),
            link: '/employees',
          },
          {
            title: i18n.t('Navigation.employeesRanking'),
            link: '/employeesRanking',
          },
        ],
      }
    : null
  ),
  {
    icon: mdiClipboardCheckMultipleOutline,
    title: i18n.t('OnlineTransactionReports.title'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.stats'),
        link: '/ordersStatistic',
      },
      {
        title: i18n.t('Navigation.notyfication'),
        link: '/stats',
      },
      {
        title: i18n.t('Navigation.orders'),
        link: '/orders',
      },
      {
        title: i18n.t('OnlineTransactionReports.title2'),
        link: '/onlineTransactionReports',
      },
      {
        title: i18n.t('PromotionsReports.title'),
        link: '/promotionsReports',
      },
    ],
  },
  {
    icon: mdiTicketPercentOutline,
    title: i18n.t('Navigation.coupons'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.coupons'),
        link: '/coupons',
      },
      {
        title: i18n.t('Navigation.couponStatistics'),
        link: '/promo-code-statistics',
      },
      {
        title: i18n.t('Navigation.couponsUsed'),
        link: '/promo-code-usage',
      },
    ],
  },
  (documentsAccess.value
    ? {
        icon: mdiFileSign,
        title: i18n.t('Navigation.documents'),
        link: '/documents',
      }
    : null),
  (deliversOrdersAccess.value
    ? {
        icon: mdiTruckDelivery,
        title: i18n.t('Navigation.deliversOrders'),
        link: '/deliversOrders',
      }
    : null
  ),
  {
    icon: mdiFinance,
    title: i18n.t('Navigation.settlement'),
    link: '/summary',
  },
  (changeLogAccess.value
    ? {
        icon: mdiFileArrowLeftRightOutline,
        title: i18n.t('Navigation.changeReport'),
        link: '/change-report',
      }
    : null),
  // (localizationsAccess.value
  //   ? {
  //       icon: mdiMapMarkerPath,
  //       title: i18n.t('Navigation.localization'),
  //       link: '/localizations',
  //     }
  //   : null),
].filter(item => item !== null))
const DefaultPanel = computed(() => [
  {
    icon: mdiLogout,
    title: i18n.t('Navigation.sign-out'),
    link: '/sign-out',
  },
])
const CustomerManagerPanel = computed(() => [
  {
    icon: mdiAccountGroup,
    title: i18n.t('Navigation.employees'),
    link: '/subsidyBudget',
  },
  {
    icon: mdiCityVariantOutline,
    title: i18n.t('Navigation.suppliersSubsidy'),
    link: '/customersSubsidy',
  },
])
const AdminPanel = computed(() => [
  {
    icon: mdiOfficeBuilding,
    title: i18n.t('Navigation.company'),
    link: '/company',
  },
  {
    icon: mdiRoutes,
    title: i18n.t('Navigation.routes'),
    link: '/routes',
  },
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('Navigation.suppliers'),
    subGroup: true,
    items: [
      {
        title: i18n.t('table.search'),
        link: '/suppliers',
      },
      {
        title: i18n.t('Navigation.map'),
        link: '/suppliersMap',
      },
    ],
  },
  {
    icon: mdiAccountGroup,
    title: i18n.t('Navigation.employees'),
    link: '/employees',
    subGroup: true,
    items: [
      {
        icon: mdiAccountGroup,
        title: i18n.t('Navigation.employees'),
        link: '/employees',
      },
      {
        title: i18n.t('Navigation.employeesRanking'),
        link: '/employeesRanking',
      },
    ],
  },
  {
    icon: mdiFoodForkDrink,
    title: i18n.t('Navigation.menu'),
    link: '/menu',
  },
  {
    icon: mdiClipboardArrowLeft,
    title: i18n.t('Navigation.menuReturns'),
    link: '/menuReturn',
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.menuReturns'),
        link: '/menuReturn',
      },
      {
        title: i18n.t('Navigation.menuReturnsReport'),
        link: '/menuReturnReport',
      },
    ],
  },
  {
    icon: mdiTruckDelivery,
    title: i18n.t('Navigation.deliversOrders'),
    link: '/deliversOrders',
  },
  {
    icon: mdiCalendarClock,
    title: i18n.t('Navigation.news'),
    link: '/news',
  },
  {
    icon: mdiFileDocumentMultiple,
    title: i18n.t('Navigation.invoices'),
    link: '/invoices',
  },
  {
    icon: mdiFileSign,
    title: i18n.t('Navigation.documents'),
    link: '/documents',
  },
  // {
  //   icon: mdiMapMarkerPath,
  //   title: i18n.t('Navigation.localization'),
  //   link: '/localizations',
  // },
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('DeliversSellsView.title'),
    link: '/delivers-sells',
  },
  {
    icon: mdiCashRegister,
    title: i18n.t('PaymentsLastWorkingDay.title'),
    link: '/payment-last-working-day',
  },
  {
    icon: mdiCityVariantOutline,
    title: i18n.t('Navigation.facilities'),
    subGroup: true,
    items: [
      {
        icon: mdiCityVariantOutline,
        title: i18n.t('Navigation.suppliersSubsidy'),
        link: '/suppliersSubsidy',
      },
      {
        icon: mdiHandshake,
        title: i18n.t('Navigation.subsidy'),
        link: '/subsidy',
      },
    ],
  },
  {
    icon: mdiTicketPercentOutline,
    title: i18n.t('Navigation.coupons'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.coupons'),
        link: '/coupons',
      },
      {
        title: i18n.t('Navigation.couponStatistics'),
        link: '/promo-code-statistics',
      },
      {
        title: i18n.t('Navigation.couponsUsed'),
        link: '/promo-code-usage',
      },
    ],
  },
  {
    icon: mdiClipboardCheckMultipleOutline,
    title: i18n.t('OnlineTransactionReports.title'),
    subGroup: true,
    items: [
      {
        title: i18n.t('Navigation.stats'),
        link: '/ordersStatistic',
      },
      {
        title: i18n.t('Navigation.notyfication'),
        link: '/stats',
      },
      {
        title: i18n.t('Navigation.orders'),
        link: '/orders',
      },
      {
        title: i18n.t('OnlineTransactionReports.title2'),
        link: '/onlineTransactionReports',
      },
      {
        title: i18n.t('PromotionsReports.title'),
        link: '/promotionsReports',
      },
    ],
  },
  {
    icon: mdiFileArrowLeftRightOutline,
    title: i18n.t('Navigation.changeReport'),
    link: '/change-report',
  },
  {
    icon: mdiFinance,
    title: i18n.t('Navigation.settlement'),
    link: '/summary',
  },
])

const AccountantPanel = computed(() => [
  {
    icon: mdiOfficeBuildingMarkerOutline,
    title: i18n.t('DeliversSellsView.title'),
    link: '/delivers-sells',
  },
])

const navigationWithoutCompany = computed(() => [
  {
    icon: mdiOfficeBuilding,
    title: i18n.t('Navigation.company'),
    link: '/company',
  },
  { divider: true },
  {
    icon: mdiLogout,
    title: i18n.t('Navigation.sign-out'),
    link: '/sign-out',
  },
])

const reportBugOrSuggestionStore = useReportBugOrSuggestionStore()
const {
  unsubscribe,
  reportBugOrSuggestion,
  reportBugOrSuggestionSolved,
  reportBugOrSuggestionActive,
  reportBugOrSuggestionWaitingCustomerAnswer,
  reportBugOrSuggestionWithoutCities,
} = storeToRefs(reportBugOrSuggestionStore)

const companyStore = useCompanyStore()
const { company } = storeToRefs(companyStore)

const messagesStore = useMessagesStore()
const { cateringOrdersToDo, unsubscribe1, unsubscribe2 } = storeToRefs(messagesStore)

// @ts-expect-error
const reportBugOrSuggestionCmp = computed(() => [
  ...(reportBugOrSuggestion.value || []),
  ...(reportBugOrSuggestionSolved.value || []),
  ...(reportBugOrSuggestionActive.value || []),
  ...(reportBugOrSuggestionWaitingCustomerAnswer.value || []),
  ...(reportBugOrSuggestionWithoutCities.value || []),
] || [])

const isReportBugOrSuggestion = computed(() => {
  const isActiveFilter = (item: ReportBugOrSuggestionModel) =>
    item?.status == 'active' || item?.status == 'solved'
  return (reportBugOrSuggestionCmp.value || []).filter(isActiveFilter).length
})

const isCateringOrderToDo = computed(() => {
  return (cateringOrdersToDo.value || []).length
})

const agreementText = computed(() =>
  (isOwner(userData.value?.role)
    ? i18n.t('CompanyView.agreementDescriptionOwner')
    : i18n.t('CompanyView.agreementDescription')),
)

const menuItems = computed(() => {
  let additionalNav: {
    icon: string
    title: string
    subGroup?: boolean
    link?: string
    items?: {
      icon?: string
      title: string
      link: string
    }[]
    divider?: boolean
  }[] = []

  if (isSandwiczSzop(userData.value?.company?.id)) {
    additionalNav = [
      {
        icon: mdiPuzzle,
        title: i18n.t('OnlineTransactionReports.title3').toString(),
        subGroup: true,
        items: [
          {
            icon: mdiBook,
            title: i18n.t('MissingCompaniesView.title').toString(),
            link: '/missingCompanies',
          },
        ],
      },
    ]
  }

  if (user.value.displayName == ROLE.CUSTOMER && userData.value.role == ROLE.MANAGER)
    return [...CustomerManagerPanel.value, ...additionalNav]

  switch (userData.value?.role) {
    case ROLE.OWNER:
      if (userData.value?.company)
        return [...AdminPanel.value, ...additionalNav]
      return navigationWithoutCompany.value
    case ROLE.GET_DELIVER:
      return [...GetDeliverPanel.value, ...additionalNav]
    case ROLE.DELIVER:
      return [...DeliverPanel.value, ...additionalNav]
    case ROLE.MANAGER:
      return [...ManagerPanel.value, ...additionalNav]
    case ROLE.MODERATOR:
      return ModeratorPanel.value
    case ROLE.BILLER:
      return BillerPanel.value
    case ROLE.ACCOUNTANT:
      return AccountantPanel.value
    default:
      return DefaultPanel.value
  }
})

function onShowAgreementDialog() {
  if (
    company.value
    && userData.value
    && !unsubscribe.value
    && userData.value?.profile?.cities
  ) {
    reportBugOrSuggestionStore.getReportBugOrSuggestion(userData.value.company, userData.value.profile.cities, userData.value.role)
  }

  if (
    userData.value
    && company.value?.agreement
    // @ts-expect-error
    && Object.values(company.value?.agreement).includes(false)
  ) {
    const timeToAcceptAgreement = mapDateFromFirebaseFormat(
      // @ts-expect-error
      company.value?.timeToAcceptAgreement,
    )

    if (localStorage?.timeToAcceptAgreement !== timeToAcceptAgreement?.toString())
      showAgreementDialog.value = true
  }
}

function close() {
  localStorage.timeToAcceptAgreement = mapDateFromFirebaseFormat(
    // @ts-expect-error
    company.value?.timeToAcceptAgreement,
  )

  showAgreementDialog.value = false
}

function toggleTheme2() {
  setTheme(!isDark.value)
  localStorage.dark = JSON.stringify(!isDark.value)
  isDark.value = !isDark.value
}

function getCateringOrdersData() {
  if (!unsubscribe1.value && userData.value?.role !== ROLE.DELIVER)
    messagesStore.getCateringOrdersToDo()

  if (!unsubscribe2.value && company.value && company.value.reference && userData.value?.role !== ROLE.DELIVER)
    messagesStore.getCompanyCateringOrders(company.value.reference)
}
watch(company, () => {
  if (company.value) {
    onShowAgreementDialog()
    getCateringOrdersData()
  }
})

onMounted(() => {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')

  const isDarkData
    = localStorage.dark == null ? mq.matches : JSON.parse(localStorage.dark)
  isDark.value = isDarkData
  if (company.value) {
    onShowAgreementDialog()
    getCateringOrdersData()
  }
})

function onChange(drawer: boolean) {
  drawerStore.setDrawer(drawer)
}

// @ts-expect-error checked
const version = __APP_VERSION__

// const isGd = computed(() => {
// // @ts-expect-error checked
//   return import.meta.env.VITE_APP === 'gd'
// })
</script>

<!-- eslint-disable max-lines -->
<template>
  <v-navigation-drawer
    app
    class="d-print-none"
    :value="visible"
    width="245px"
    @input="onChange"
  >
    <v-list
      dense
      shaped
    >
      <v-list-item>
        <v-row justify="center">
          {{ $t("App.title1") }}
          <!--
            <h3
            v-if="isGd"
            :dark="isDark"
            >
            {{ $t("App.title1") }}
            </h3>

            <h3
            v-else
            :dark="isDark"
            >
            {{ $t("App.title2") }}
            </h3>
          -->
        </v-row>
      </v-list-item>

      <v-list-item
        v-if="company && !isAccountant(userData?.role)"
        color="secondary"
        to="/reportBugOrSuggestion"
      >
        <v-badge
          v-if="isReportBugOrSuggestion"
          color="red"
          :content="isReportBugOrSuggestion"
        >
          <v-icon>
            {{ mdiBell }}
          </v-icon>
        </v-badge>

        <v-icon v-else>
          {{ mdiBell }}
        </v-icon>

        <span class="pl-4 text-subtitle-2">
          {{ $t("Navigation.reportBugOrSuggestion") }}
        </span>
      </v-list-item>

      <v-list-item
        v-if="company && !isAccountant(userData?.role)"
        key="v-list-item-2"
        color="secondary"
        to="/catering-orders"
      >
        <v-badge
          v-if="isCateringOrderToDo"
          color="red"
          :content="isCateringOrderToDo"
        >
          <v-icon>
            {{ mdiCartVariant }}
          </v-icon>
        </v-badge>

        <v-icon v-else>
          {{ mdiCartVariant }}
        </v-icon>

        <span class="pl-4 text-subtitle-2">
          {{ $t("Navigation.cateringOrders") }}
        </span>
      </v-list-item>

      <v-list-item-group>
        <TheMenuItem
          v-for="(item, index) in menuItems"
          :key="index"
          :item="item"
        />
      </v-list-item-group>
    </v-list>

    <v-spacer />

    <template #append>
      <v-divider />

      <v-list-item
        color="secondary"
        dense
        @click.stop="toggleTheme2"
      >
        <v-icon>
          {{ isDark
            ? mdiWeatherSunny
            : mdiWeatherNight }}
        </v-icon>

        <span class="pl-4 text-subtitle-2">
          {{ $t("darkTheme") }}
        </span>
      </v-list-item>

      <v-divider />

      <v-list-item
        color="secondary"
        dense
        to="/sign-out"
      >
        <v-icon>{{ mdiLogout }}</v-icon>

        <span class="pl-4 text-subtitle-2">
          {{ $t("Navigation.sign-out") }}
        </span>
      </v-list-item>

      <v-divider />

      <v-list-item
        class="text-subtitle-2"
        dense
      >
        {{ version }}
      </v-list-item>
    </template>

    <v-dialog
      v-model="showAgreementDialog"
      max-width="600px"
      scrollable
      @click.stop="close"
      @click:outside="close"
      @keydown.esc="close"
    >
      <TheDialogInfo
        :loading="false"
        :on-close="close"
        :title="$t('CompanyView.agreementInfo').toString()"
      >
        <span>
          {{ agreementText }}
        </span>
      </TheDialogInfo>
    </v-dialog>
  </v-navigation-drawer>
</template>
